import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The ADVisor loading standard: skeletons, never spinners.
 *
 * A skeleton takes the shape of the content that is coming — same heights, same
 * radii, line widths varied 44–92% so a stack never reads as a grid. A spinner
 * says "something is happening"; a skeleton says what is about to arrive, and
 * the page does not jump when it does.
 *
 * `Spinner` is still legal in exactly two places: inside a submitting button,
 * and the auth bootstrap. Everywhere else, reach for this.
 *
 * The shimmer itself lives in `.adv-skeleton` (src/styles/tokens/base.css) —
 * it needs the gradient and the 560px band, so `animate-shimmer` alone is not
 * enough. `prefers-reduced-motion` switches it off and leaves the flat shape.
 */
export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Set false for containers that frame shimmering children but do not shimmer themselves. */
  shimmer?: boolean;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, shimmer = true, ...props }, ref) => (
    <div
      ref={ref}
      aria-hidden
      className={cn(
        "rounded-md",
        shimmer ? "adv-skeleton" : "bg-neutral-100",
        className,
      )}
      {...props}
    />
  ),
);
Skeleton.displayName = "Skeleton";

/**
 * A paragraph-shaped stack. The default widths are deliberately uneven — equal
 * widths read as a table rather than as prose that has not loaded.
 */
export interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  lines?: number;
  /** Percentages, cycled across the lines. */
  widths?: number[];
}

const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  ({ className, lines = 3, widths = [92, 68, 44], ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-3 rounded-sm"
          style={{ width: `${widths[i % widths.length] ?? 70}%` }}
        />
      ))}
    </div>
  ),
);
SkeletonText.displayName = "SkeletonText";

export { Skeleton, SkeletonText };
